import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Plus, 
  Edit, 
  Trash2, 
  GripVertical,
  Loader2,
  Zap,
  AlertCircle,
  Layers,
  LayoutGrid,
  Info,
  MapPin,
  MousePointer2,
  Settings2,
  CheckCircle2,
  Archive,
  RefreshCw,
  Eye,
  EyeOff,
  AlertTriangle,
  Wrench
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { Toaster } from '../../components/ui/toaster';
import { useNavigate } from 'react-router-dom';
import { 
  getSlotsForTemplate, 
  getButtonsForTemplate,
  getButtonByKey,
  getButtonDefaultLabel,
  getSlotForButton
} from '../../templates';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'inverser_selected_campaign';

const ActionsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [forceDeleteConfirmOpen, setForceDeleteConfirmOpen] = useState(false);
  const [retireConfirmOpen, setRetireConfirmOpen] = useState(false);
  const [replaceModalOpen, setReplaceModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedActionLinkInfo, setSelectedActionLinkInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showRetired, setShowRetired] = useState(false);
  const [cleaningOrphans, setCleaningOrphans] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Replace action state
  const [replaceData, setReplaceData] = useState({
    newActionKey: ''
  });

  // Form state - OPCIÓN B: slot único
  const [formData, setFormData] = useState({
    action_key: '',
    button_key: '',
    label: '',
    description: '',
    internal_note: '',
    active: true,
    order: 0,
    slot: ''  // OPCIÓN B: slot único (viene del botón del template)
  });
  const [formErrors, setFormErrors] = useState({});

  // Get template info for current campaign
  const templateKey = selectedCampaign?.template_key || 'cpn';
  const availableSlots = getSlotsForTemplate(templateKey);
  const availableButtons = getButtonsForTemplate(templateKey);
  
  // OPCIÓN B: El slot viene del botón seleccionado (solo lectura)
  const selectedButton = formData.button_key 
    ? getButtonByKey(templateKey, formData.button_key) 
    : null;

  // Load campaigns on mount
  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Load actions when campaign changes
  useEffect(() => {
    if (selectedCampaign) {
      fetchActions(selectedCampaign.key);
      localStorage.setItem(STORAGE_KEY, selectedCampaign.key);
    }
  }, [selectedCampaign, showRetired]);

  // Reset form data when button changes (to ensure clean state)
  useEffect(() => {
    if (formData.button_key && selectedButton && modalOpen) {
      // Only update if we're in the modal and have a selected button
      // This ensures proper re-render when switching between buttons
      const defaultLabel = selectedButton.label_default || formData.button_key;
      
      setFormData(prev => ({
        ...prev,
        // Keep existing values if editing, otherwise use defaults
        label: selectedAction ? prev.label : (prev.label || defaultLabel),
        // OPCIÓN B: slot único y fijo del botón
        slot: selectedButton.slot
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.button_key, modalOpen]);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/campaigns?active_only=true`);
      const activeCampaigns = response.data;
      setCampaigns(activeCampaigns);
      
      if (activeCampaigns.length > 0) {
        const savedCampaignKey = localStorage.getItem(STORAGE_KEY);
        const savedCampaign = activeCampaigns.find(c => c.key === savedCampaignKey);
        setSelectedCampaign(savedCampaign || activeCampaigns[0]);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las campañas',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchActions = useCallback(async (campaignKey) => {
    setActionsLoading(true);
    try {
      const url = `${BACKEND_URL}/api/admin/actions?campaign_key=${campaignKey}&include_retired=${showRetired}`;
      const response = await axios.get(url);
      setActions(response.data);
    } catch (error) {
      console.error('Error fetching actions:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las acciones',
        variant: 'destructive'
      });
    } finally {
      setActionsLoading(false);
    }
  }, [toast]);

  const handleCampaignChange = (campaignKey) => {
    const campaign = campaigns.find(c => c.key === campaignKey);
    if (campaign) {
      setSelectedCampaign(campaign);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.button_key) {
      errors.button_key = 'Selecciona un botón del template';
    }
    
    if (!formData.action_key) {
      errors.action_key = 'El key es requerido';
    } else if (!/^[a-z0-9_-]+$/.test(formData.action_key)) {
      errors.action_key = 'Solo letras minúsculas, números, guiones y guiones bajos';
    } else if (!selectedAction) {
      const exists = actions.some(a => a.action_key === formData.action_key);
      if (exists) {
        errors.action_key = 'Este key ya existe en esta campaña';
      }
    }
    
    if (!formData.label) {
      errors.label = 'El texto del botón es requerido';
    }

    // OPCIÓN B: Validar que hay un slot (viene automáticamente del botón)
    if (!formData.slot && selectedButton) {
      // Auto-asignar el slot del botón si no está definido
      formData.slot = selectedButton.slot;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openModal = (action = null) => {
    if (action) {
      setSelectedAction(action);
      // OPCIÓN B: slot viene del botón del template
      const button = getButtonByKey(templateKey, action.button_key);
      setFormData({
        action_key: action.action_key,
        button_key: action.button_key || action.action_key,
        label: action.label,
        description: action.description || '',
        internal_note: action.internal_note || '',
        active: action.active,
        order: action.order || 0,
        slot: button?.slot || action.slot || ''
      });
    } else {
      setSelectedAction(null);
      setFormData({
        action_key: '',
        button_key: '',
        label: '',
        description: '',
        internal_note: '',
        active: true,
        order: actions.length,
        slot: ''
      });
    }
    setFormErrors({});
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedAction(null);
    setFormErrors({});
  };

  const handleButtonSelect = (buttonKey) => {
    const button = getButtonByKey(templateKey, buttonKey);
    if (button) {
      // OPCIÓN B: Completar form con datos del botón (slot es fijo)
      setFormData(prev => ({
        ...prev,
        button_key: buttonKey,
        action_key: buttonKey,
        label: button.label_default,
        description: button.description || '',
        slot: button.slot  // OPCIÓN B: slot único y fijo
      }));
      
      // Clear related form errors
      setFormErrors(prev => ({ 
        ...prev, 
        button_key: null,
        action_key: null,
        label: null
      }));
    }
  };

  // OPCIÓN B: handleSlotToggle ya no es necesario (slot es fijo)
  // Se mantiene como no-op por si hay referencias pendientes
  const handleSlotToggle = () => {
    // No-op: En Opción B el slot es fijo y viene del botón del template
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || !selectedCampaign) return;
    
    setSaving(true);
    try {
      if (selectedAction) {
        // OPCIÓN B: Enviar slot único
        await axios.put(`${BACKEND_URL}/api/admin/actions/${selectedAction.id}`, {
          label: formData.label,
          button_key: formData.button_key,
          description: formData.description || null,
          internal_note: formData.internal_note || null,
          active: formData.active,
          order: formData.order,
          slot: formData.slot
        });
        toast({ title: 'Actualizado', description: 'Acción actualizada correctamente' });
      } else {
        // OPCIÓN B: Enviar slot único
        await axios.post(`${BACKEND_URL}/api/admin/actions`, {
          campaign_key: selectedCampaign.key,
          action_key: formData.action_key,
          button_key: formData.button_key,
          label: formData.label,
          description: formData.description || null,
          internal_note: formData.internal_note || null,
          active: formData.active,
          order: formData.order,
          slot: formData.slot
        });
        toast({ title: 'Creado', description: 'Acción creada correctamente' });
      }
      
      closeModal();
      fetchActions(selectedCampaign.key);
    } catch (error) {
      console.error('Error saving action:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo guardar la acción',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (action) => {
    setSelectedAction(action);
    // Fetch link info to show accurate data
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/actions/${action.id}/link-count`);
      setSelectedActionLinkInfo(response.data);
    } catch (error) {
      setSelectedActionLinkInfo({ valid: 0, orphan: 0, empty_url: 0, total: 0 });
    }
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async (force = false) => {
    if (!selectedAction) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/actions/${selectedAction.id}?force=${force}`);
      toast({ 
        title: 'Eliminado', 
        description: force 
          ? 'Acción y todos sus enlaces eliminados correctamente'
          : 'Acción eliminada correctamente' 
      });
      fetchActions(selectedCampaign.key);
    } catch (error) {
      console.error('Error deleting action:', error);
      const detail = error.response?.data?.detail || 'No se pudo eliminar la acción';
      
      // If delete failed due to valid links, offer force delete
      if (detail.includes('URLs activas') || detail.includes('mentor(es)')) {
        setDeleteConfirmOpen(false);
        setForceDeleteConfirmOpen(true);
        return;
      }
      
      toast({
        title: 'Error',
        description: detail,
        variant: 'destructive'
      });
    } finally {
      setDeleteConfirmOpen(false);
      setSelectedAction(null);
      setSelectedActionLinkInfo(null);
    }
  };

  const handleForceDelete = async () => {
    await handleDelete(true);
    setForceDeleteConfirmOpen(false);
  };

  // Cleanup orphan data
  const handleCleanupOrphans = async () => {
    if (!selectedCampaign) {
      toast({
        title: 'Error',
        description: 'Selecciona una campaña primero',
        variant: 'destructive'
      });
      return;
    }
    
    setCleaningOrphans(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/actions/cleanup-orphans?campaign_key=${selectedCampaign.key}`
      );
      const deleted = response.data.deleted;
      toast({
        title: 'Limpieza completada',
        description: `Campaña "${selectedCampaign.name}": ${deleted.orphan_links_deleted} links huérfanos, ${deleted.empty_url_links_deleted} links vacíos, ${deleted.orphan_tokens_deleted} tokens huérfanos eliminados`
      });
      // Refresh actions to update counts
      fetchActions(selectedCampaign.key);
    } catch (error) {
      console.error('Error cleaning orphans:', error);
      toast({
        title: 'Error',
        description: 'No se pudo realizar la limpieza',
        variant: 'destructive'
      });
    } finally {
      setCleaningOrphans(false);
    }
  };

  // Retire action (soft delete)
  const confirmRetire = (action) => {
    setSelectedAction(action);
    setRetireConfirmOpen(true);
  };

  const handleRetire = async () => {
    if (!selectedAction) return;
    
    try {
      await axios.post(`${BACKEND_URL}/api/admin/actions/${selectedAction.id}/retire`);
      toast({ title: 'Archivado', description: 'Acción archivada correctamente' });
      fetchActions(selectedCampaign.key);
    } catch (error) {
      console.error('Error retiring action:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo archivar la acción',
        variant: 'destructive'
      });
    } finally {
      setRetireConfirmOpen(false);
      setSelectedAction(null);
    }
  };

  // Replace action (migrate links)
  const openReplaceModal = (action) => {
    setSelectedAction(action);
    setReplaceData({ newActionKey: '' });
    setReplaceModalOpen(true);
  };

  const handleReplace = async () => {
    if (!selectedAction || !replaceData.newActionKey || !selectedCampaign) return;
    
    setSaving(true);
    try {
      const result = await axios.post(`${BACKEND_URL}/api/admin/actions/replace`, {
        campaign_key: selectedCampaign.key,
        old_action_key: selectedAction.action_key,
        new_action_key: replaceData.newActionKey,
        migrate_links: true,
        migrate_events: false
      });
      
      toast({ 
        title: 'Migración completada', 
        description: `${result.data.links_migrated} enlaces migrados de "${selectedAction.action_key}" a "${replaceData.newActionKey}"`
      });
      
      setReplaceModalOpen(false);
      setSelectedAction(null);
      fetchActions(selectedCampaign.key);
    } catch (error) {
      console.error('Error replacing action:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo realizar la migración',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (action) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/actions/${action.id}`, {
        active: !action.active
      });
      toast({
        title: 'Actualizado',
        description: `Acción ${action.active ? 'desactivada' : 'activada'} correctamente`
      });
      fetchActions(selectedCampaign.key);
    } catch (error) {
      console.error('Error toggling action:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la acción',
        variant: 'destructive'
      });
    }
  };

  // Helper to get slot label (OPCIÓN B: slot único)
  const getSlotLabel = (slotKey) => {
    if (!slotKey) return 'Sin ubicación';
    const slot = availableSlots.find(s => s.key === slotKey);
    return slot ? slot.label.replace('Hero - ', '').replace('CTA ', '') : slotKey;
  };

  // Helper to get button info
  const getButtonInfo = (buttonKey) => {
    const button = getButtonByKey(templateKey, buttonKey);
    return button || { label_default: buttonKey, description: '', slot: null };
  };

  // Check which buttons are already configured
  const configuredButtonKeys = actions.map(a => a.button_key || a.action_key);
  const unconfiguredButtons = availableButtons.filter(b => !configuredButtonKeys.includes(b.key));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#7c3aed]" />
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="max-w-4xl mx-auto">
        <Toaster />
        <Card className="p-12 text-center">
          <Layers className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No hay campañas activas</h2>
          <p className="text-gray-600 mb-6">
            Para gestionar acciones, primero necesitas crear al menos una campaña activa.
          </p>
          <Button onClick={() => navigate('/admin/campaigns')} className="bg-[#7c3aed] hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            Ir a Campañas
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-display font-bold text-gray-900">Configurar Botones</h1>
          <p className="text-gray-600 mt-1">
            Configura los botones del template para que los mentores puedan personalizar sus URLs
          </p>
        </div>
        
        {/* Campaign Selector */}
        <div className="flex items-center gap-3">
          <Label className="text-sm font-medium whitespace-nowrap">Campaña:</Label>
          <Select value={selectedCampaign?.key || ''} onValueChange={handleCampaignChange}>
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder="Seleccionar campaña" />
            </SelectTrigger>
            <SelectContent>
              {campaigns.map((campaign) => (
                <SelectItem key={campaign.key} value={campaign.key}>
                  {campaign.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Context Banner */}
      {selectedCampaign && (
        <Card className="p-4 mb-6 bg-purple-50 border-purple-200">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-3">
              <Zap className="w-5 h-5 text-[#7c3aed]" />
              <div>
                <p className="font-medium text-purple-900">
                  Campaña: <span className="font-bold">{selectedCampaign.name}</span>
                </p>
                <p className="text-sm text-purple-700 flex items-center gap-3">
                  <span>Template: <strong>{templateKey}</strong></span>
                  <span>•</span>
                  <span className="flex items-center">
                    <MousePointer2 className="w-3 h-3 mr-1" />
                    {availableButtons.length} botones en el diseño
                  </span>
                  <span>•</span>
                  <span className="flex items-center">
                    <LayoutGrid className="w-3 h-3 mr-1" />
                    {availableSlots.length} ubicaciones
                  </span>
                </p>
              </div>
            </div>
            <Button
              onClick={() => openModal()}
              className="bg-[#7c3aed] hover:bg-purple-700"
              disabled={unconfiguredButtons.length === 0}
            >
              <Plus className="w-4 h-4 mr-2" />
              Configurar Botón
            </Button>
          </div>
        </Card>
      )}

      {/* Info Card */}
      <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">¿Cómo funciona?</p>
            <p>
              El template <strong>{templateKey}</strong> tiene <strong>{availableButtons.length} botones</strong> predefinidos en su diseño.
              Aquí configuras cada botón para que los mentores puedan asignar sus URLs personalizadas.
            </p>
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="bg-blue-100 px-2 py-1 rounded">BOTÓN (del diseño)</span>
              <span>→</span>
              <span className="bg-blue-100 px-2 py-1 rounded">ACCIÓN (configuración)</span>
              <span>→</span>
              <span className="bg-blue-100 px-2 py-1 rounded">SLOT (ubicación)</span>
              <span>→</span>
              <span className="bg-blue-100 px-2 py-1 rounded">URL (del mentor)</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Unconfigured Buttons Alert */}
      {unconfiguredButtons.length > 0 && (
        <Card className="p-4 mb-6 bg-amber-50 border-amber-200">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">Botones sin configurar</p>
              <p>
                Estos botones existen en el template pero no están configurados:
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {unconfiguredButtons.map(button => (
                  <Badge 
                    key={button.key} 
                    variant="outline" 
                    className="bg-white border-amber-300 text-amber-700 cursor-pointer hover:bg-amber-100"
                    onClick={() => {
                      handleButtonSelect(button.key);
                      setModalOpen(true);
                    }}
                  >
                    + {button.label_default}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Toggle show retired + Actions List Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Botones Configurados ({actions.filter(a => a.status !== 'retired').length})
        </h2>
        <div className="flex items-center gap-4">
          <Button
            onClick={handleCleanupOrphans}
            variant="outline"
            size="sm"
            disabled={cleaningOrphans}
            className="text-gray-600 hover:text-gray-800"
            title="Limpia enlaces de mentores eliminados y URLs vacías"
          >
            {cleaningOrphans ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Wrench className="w-4 h-4 mr-2" />
            )}
            Limpiar datos huérfanos
          </Button>
          <div className="flex items-center gap-2">
            <Label htmlFor="show-retired" className="text-sm text-gray-600">Ver archivados</Label>
            <Switch
              id="show-retired"
              checked={showRetired}
              onCheckedChange={setShowRetired}
            />
          </div>
        </div>
      </div>

      {/* Actions List */}
      {actionsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#7c3aed]" />
        </div>
      ) : actions.length === 0 ? (
        <Card className="p-12 text-center">
          <MousePointer2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Sin botones configurados
          </h3>
          <p className="text-gray-600 mb-6">
            El template tiene {availableButtons.length} botones disponibles. 
            Configura cada uno para que los mentores puedan personalizar sus URLs.
          </p>
          <Button onClick={() => openModal()} className="bg-[#7c3aed] hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            Configurar primer botón
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {actions.map((action) => {
            const buttonInfo = getButtonInfo(action.button_key || action.action_key);
            const isRetired = action.status === 'retired';
            
            return (
              <Card key={action.id} className={`p-4 hover:shadow-md transition-shadow ${isRetired ? 'opacity-60 bg-gray-50' : ''}`}>
                <div className="flex items-center gap-4">
                  <div className="text-gray-400 cursor-move">
                    <GripVertical className="w-5 h-5" />
                  </div>
                  
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600">
                    {action.order}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className={`font-semibold ${isRetired ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{action.label}</h3>
                      <Badge variant="secondary" className="font-mono text-xs bg-purple-100 text-purple-700">
                        {action.button_key || action.action_key}
                      </Badge>
                      {isRetired ? (
                        <Badge variant="outline" className="bg-gray-200 text-gray-600">
                          <Archive className="w-3 h-3 mr-1" />
                          Archivada
                        </Badge>
                      ) : (
                        <Badge variant={action.active ? "default" : "outline"}>
                          {action.active ? 'Activa' : 'Inactiva'}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Button description */}
                    {buttonInfo.description && (
                      <p className="text-xs text-gray-500 mt-1">{buttonInfo.description}</p>
                    )}
                    
                    {/* Slot Display - OPCIÓN B: slot único */}
                    <div className="flex items-center gap-1 mt-2 flex-wrap">
                      <MapPin className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-500">Ubicación:</span>
                      <Badge variant="outline" className="text-xs bg-purple-50 border-purple-200 text-purple-700">
                        {getSlotLabel(action.slot || getButtonInfo(action.button_key)?.slot)}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Actions - different for retired vs active */}
                  <div className="flex items-center gap-2">
                    {isRetired ? (
                      // Retired action - minimal options
                      <span className="text-xs text-gray-400">Archivada</span>
                    ) : (
                      // Active/Inactive action - full options
                      <>
                        <Button
                          onClick={() => toggleActive(action)}
                          size="sm"
                          variant="outline"
                          className={action.active ? "text-orange-600" : "text-green-600"}
                          title={action.active ? 'Desactivar' : 'Activar'}
                        >
                          {action.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button 
                          onClick={() => openModal(action)} 
                          size="sm" 
                          variant="outline"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => openReplaceModal(action)}
                          size="sm"
                          variant="outline"
                          title="Migrar enlaces a otra acción"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => confirmRetire(action)}
                          size="sm"
                          variant="outline"
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          title="Archivar (conserva datos)"
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => confirmDelete(action)}
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Eliminar definitivamente"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal - 3 Step Flow */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              {selectedAction ? 'Editar Configuración' : 'Configurar Botón'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* ============================================ */}
            {/* PASO 1: Seleccionar Botón del Template */}
            {/* ============================================ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <span className="w-6 h-6 rounded-full bg-[#7c3aed] text-white flex items-center justify-center text-xs">1</span>
                ¿Qué botón del template estás configurando?
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600 mb-2">
                Template: <strong>{templateKey}</strong> • {availableButtons.length} botones disponibles
              </div>

              <div className="space-y-2">
                {availableButtons.map((button) => {
                  const isConfigured = configuredButtonKeys.includes(button.key) && 
                    (!selectedAction || selectedAction.button_key !== button.key);
                  const isSelected = formData.button_key === button.key;
                  
                  return (
                    <div 
                      key={button.key}
                      onClick={() => !isConfigured && !selectedAction && handleButtonSelect(button.key)}
                      className={`
                        p-3 rounded-lg border-2 transition-all
                        ${isSelected 
                          ? 'border-[#7c3aed] bg-purple-50' 
                          : isConfigured 
                            ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                            : selectedAction
                              ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                              : 'border-gray-200 hover:border-purple-300 cursor-pointer'
                        }
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{button.label_default}</p>
                          <p className="text-xs text-gray-500">{button.description}</p>
                          <div className="flex gap-1 mt-1">
                            {/* OPCIÓN B: slot único */}
                            <span className="text-xs bg-gray-200 px-1.5 py-0.5 rounded">
                              {button.slot}
                            </span>
                          </div>
                        </div>
                        {isSelected && <CheckCircle2 className="w-5 h-5 text-[#7c3aed]" />}
                        {isConfigured && !isSelected && (
                          <Badge variant="outline" className="text-xs">Ya configurado</Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {formErrors.button_key && (
                <p className="text-sm text-red-500">{formErrors.button_key}</p>
              )}
            </div>

            {/* ============================================ */}
            {/* PASO 2: Personalizar Texto */}
            {/* ============================================ */}
            {formData.button_key && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <span className="w-6 h-6 rounded-full bg-[#7c3aed] text-white flex items-center justify-center text-xs">2</span>
                  Personaliza el texto (opcional)
                </div>

                <div className="space-y-3">
                  {/* Label */}
                  <div className="space-y-2">
                    <Label htmlFor="btn-label">Texto del botón</Label>
                    <Input
                      id="btn-label"
                      name="btn-label"
                      autoComplete="off"
                      value={formData.label}
                      onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                      placeholder={getButtonDefaultLabel(templateKey, formData.button_key)}
                      className={formErrors.label ? 'border-red-500' : ''}
                    />
                    {formErrors.label && (
                      <p className="text-sm text-red-500">{formErrors.label}</p>
                    )}
                    <p className="text-xs text-gray-500">
                      Default: {getButtonDefaultLabel(templateKey, formData.button_key)}
                    </p>
                  </div>

                  {/* Action Key (hidden for most users, auto-generated) */}
                  {!selectedAction && (
                    <div className="space-y-2">
                      <Label htmlFor="action_key" className="text-gray-500">
                        ID interno (auto-generado)
                      </Label>
                      <Input
                        id="action_key"
                        value={formData.action_key}
                        onChange={(e) => setFormData({ ...formData, action_key: e.target.value.toLowerCase() })}
                        placeholder={formData.button_key}
                        className={`font-mono text-sm ${formErrors.action_key ? 'border-red-500' : ''}`}
                      />
                      {formErrors.action_key && (
                        <p className="text-sm text-red-500">{formErrors.action_key}</p>
                      )}
                    </div>
                  )}

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="description">Descripción (opcional)</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Descripción pública del botón"
                    />
                  </div>

                  {/* Internal Note - visible to admin and mentor */}
                  <div className="space-y-2">
                    <Label htmlFor="internal_note">
                      Nota Interna (visible para admin y mentor)
                    </Label>
                    <Input
                      id="internal_note"
                      value={formData.internal_note}
                      onChange={(e) => setFormData({ ...formData, internal_note: e.target.value })}
                      placeholder="Ej: Aquí el mentor debe colocar su enlace de Calendly"
                    />
                    <p className="text-xs text-gray-500">
                      💡 Esta nota aparece en el panel de edición de enlaces para ayudar al admin y mentor a entender qué URL deben colocar.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================ */}
            {/* PASO 3: Ubicación (SOLO LECTURA - Opción B) */}
            {/* ============================================ */}
            {formData.button_key && selectedButton && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <span className="w-6 h-6 rounded-full bg-[#7c3aed] text-white flex items-center justify-center text-xs">3</span>
                  Ubicación del Botón
                </div>

                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-[#7c3aed]" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {availableSlots.find(s => s.key === selectedButton.slot)?.label || selectedButton.slot}
                      </p>
                      <p className="text-xs text-gray-500">
                        {availableSlots.find(s => s.key === selectedButton.slot)?.description || 'Ubicación fija definida por el diseño'}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 italic">
                    La ubicación es fija y está determinada por el diseño del template.
                  </p>
                </div>
              </div>
            )}

            {/* Order & Active */}
            {formData.button_key && (
              <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                <div className="space-y-2">
                  <Label htmlFor="order">Orden</Label>
                  <Input
                    id="order"
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
                <div className="flex items-center justify-between pt-6">
                  <Label htmlFor="active">Activa</Label>
                  <Switch
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={saving || !formData.button_key} 
                className="bg-[#7c3aed] hover:bg-purple-700"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {selectedAction ? 'Guardar' : 'Configurar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" />
              ¿Eliminar configuración?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>Esto eliminará la configuración del botón &ldquo;{selectedAction?.label}&rdquo;.</p>
                
                {selectedActionLinkInfo && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                    <p className="font-semibold text-gray-700 mb-2">Estado de enlaces:</p>
                    <ul className="space-y-1">
                      <li className="flex items-center gap-2">
                        <span className={selectedActionLinkInfo.valid > 0 ? 'text-red-600 font-semibold' : 'text-green-600'}>
                          {selectedActionLinkInfo.valid > 0 ? '⚠️' : '✅'}
                        </span>
                        <span>{selectedActionLinkInfo.valid} enlaces válidos (mentor existe + URL no vacía)</span>
                      </li>
                      {selectedActionLinkInfo.orphan > 0 && (
                        <li className="flex items-center gap-2 text-gray-500">
                          <span>🗑️</span>
                          <span>{selectedActionLinkInfo.orphan} enlaces huérfanos (se eliminarán automáticamente)</span>
                        </li>
                      )}
                      {selectedActionLinkInfo.empty_url > 0 && (
                        <li className="flex items-center gap-2 text-gray-500">
                          <span>📭</span>
                          <span>{selectedActionLinkInfo.empty_url} enlaces con URL vacía (se eliminarán automáticamente)</span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
                
                {selectedActionLinkInfo?.valid > 0 ? (
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-amber-800 text-sm">
                      <strong>⚠️ Atención:</strong> Hay {selectedActionLinkInfo.valid} mentor(es) con URLs activas.
                      <br />
                      <span className="text-xs">Si continúas, deberás usar &ldquo;Eliminar forzado&rdquo; para borrar también esos enlaces.</span>
                    </p>
                  </div>
                ) : (
                  <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-green-800 text-sm">
                      ✅ No hay enlaces válidos activos. Se puede eliminar de forma segura.
                    </p>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDelete(false)} 
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Force Delete Confirmation */}
      <AlertDialog open={forceDeleteConfirmOpen} onOpenChange={setForceDeleteConfirmOpen}>
        <AlertDialogContent className="border-red-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              ¡Eliminar Forzado!
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <div className="bg-red-50 p-3 rounded-lg border border-red-200 mt-2">
                  <p className="text-red-800 text-sm font-semibold">
                    Esta acción eliminará:
                  </p>
                  <ul className="list-disc list-inside mt-2 text-red-700 text-sm">
                    <li>La configuración del botón &ldquo;{selectedAction?.label}&rdquo;</li>
                    <li><strong>TODOS</strong> los enlaces de mentores asociados</li>
                    <li>Los datos de analítica podrían verse afectados</li>
                  </ul>
                </div>
                
                <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-amber-800 text-sm">
                    <strong>💡 Alternativa:</strong> Usa &ldquo;Archivar&rdquo; para ocultar el botón pero conservar todos los datos históricos.
                  </p>
                </div>
                
                <p className="mt-3 text-sm text-gray-600">
                  ¿Estás seguro de que quieres continuar?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <Button
              onClick={() => {
                setForceDeleteConfirmOpen(false);
                confirmRetire(selectedAction);
              }}
              variant="outline"
              className="text-amber-600 border-amber-300 hover:bg-amber-50"
            >
              <Archive className="w-4 h-4 mr-2" />
              Archivar en su lugar
            </Button>
            <AlertDialogAction 
              onClick={handleForceDelete} 
              className="bg-red-600 hover:bg-red-700"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Sí, eliminar todo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Retire Confirmation */}
      <AlertDialog open={retireConfirmOpen} onOpenChange={setRetireConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-amber-600" />
              ¿Archivar acción?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esto archivará la acción &ldquo;{selectedAction?.label}&rdquo;.
              <br /><br />
              <strong>¿Qué significa archivar?</strong>
              <ul className="list-disc list-inside mt-2 text-sm">
                <li>No aparecerá en la landing</li>
                <li>No estará disponible para mentores</li>
                <li>Se conservan los enlaces existentes (historial)</li>
                <li>No aparecerá en esta lista (a menos que actives &ldquo;Ver archivados&rdquo;)</li>
              </ul>
              <br />
              <span className="text-amber-600">Recomendado cuando el diseño cambia y quieres conservar los datos.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleRetire} className="bg-amber-600 hover:bg-amber-700">
              <Archive className="w-4 h-4 mr-2" />
              Archivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Replace Action Modal */}
      <Dialog open={replaceModalOpen} onOpenChange={setReplaceModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-600" />
              Migrar Enlaces a Otra Acción
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
              <p>
                Esto migrará <strong>todos los enlaces de mentores</strong> de la acción 
                <strong> &ldquo;{selectedAction?.label}&rdquo;</strong> a otra acción.
              </p>
              <p className="mt-2 text-xs">
                Útil cuando un botón cambia de nombre o se reemplaza por otro.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Acción origen (actual)</Label>
              <Input 
                value={selectedAction?.label || ''} 
                disabled 
                className="bg-gray-100"
              />
            </div>

            <div className="space-y-2">
              <Label>Migrar enlaces a:</Label>
              <Select 
                value={replaceData.newActionKey} 
                onValueChange={(value) => setReplaceData({ newActionKey: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar acción destino" />
                </SelectTrigger>
                <SelectContent>
                  {actions
                    .filter(a => a.action_key !== selectedAction?.action_key && a.status !== 'retired')
                    .map((action) => (
                      <SelectItem key={action.action_key} value={action.action_key}>
                        {action.label} ({action.action_key})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-800">
              <strong>Después de migrar:</strong> puedes archivar la acción original.
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setReplaceModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleReplace} 
              disabled={saving || !replaceData.newActionKey}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <RefreshCw className="w-4 h-4 mr-2" />
              Migrar Enlaces
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActionsPage;
